import type * as React from 'react';
import type { Button } from '../../../button';
import type { Input } from '../../../input';
import type { Separator } from '../../../separator';
import type { TooltipContent } from '../../../tooltip';

// ============================================================================
// Constants
// ============================================================================

/**
 * localStorage key for sidebar expanded state.
 * Actual persistence is handled by useSidebarState hook in hooks/usePageShellState.ts
 */
export const SIDEBAR_STORAGE_KEY = 'heidi-sidebar-expanded';

export const SIDEBAR_WIDTH = '12rem';
export const SIDEBAR_WIDTH_MOBILE = '18rem';
export const SIDEBAR_WIDTH_ICON = '3rem';
export const SIDEBAR_WIDTH_LABEL_ICONS = '4.5rem';
export const SIDEBAR_WIDTH_NAV_V3 = '17rem';
export const SIDEBAR_KEYBOARD_SHORTCUT = 'h';

/**
 * Total height of the header bar (sidebar header and content header)
 * = p-2 padding (1rem) + SIDEBAR_HEADER_ICON_SIZE (2rem) = 3rem
 */
export const SIDEBAR_HEADER_HEIGHT = '3rem';

/**
 * Size of icon containers in sidebar header (logo, trigger button)
 * Used to ensure horizontal alignment between sidebar and content header
 */
export const SIDEBAR_HEADER_ICON_SIZE = '2rem';

// ============================================================================
// Context Types
// ============================================================================

export type SidebarState = 'expanded' | 'collapsed';

export type SidebarContextProps = {
  state: SidebarState;
  open: boolean;
  setOpen: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  collapsible: SidebarCollapsible;
  setCollapsible: (collapsible: SidebarCollapsible) => void;
  variant: SidebarVariant;
  setVariant: (variant: SidebarVariant) => void;
};

// ============================================================================
// Component Props
// ============================================================================

export interface SidebarProviderProps extends React.ComponentProps<'div'> {
  /**
   * Default open state for uncontrolled usage
   * @default false
   */
  defaultOpen?: boolean;

  /**
   * Controlled open state
   */
  open?: boolean;

  /**
   * Callback when open state changes
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Force mobile mode regardless of window width.
   * Used by MobileSidebar to ensure mobile behavior in all contexts.
   * @default false
   */
  forceMobile?: boolean;

  /**
   * Default collapsible mode for initial render.
   * Set this to match the Sidebar's collapsible prop to prevent animation flicker on mount.
   * @default 'icon'
   */
  defaultCollapsible?: SidebarCollapsible;
}

export type SidebarVariant = 'sidebar' | 'floating' | 'inset';
export type SidebarCollapsible = 'icon' | 'label-icons' | 'offcanvas' | 'none';

export interface SidebarProps extends React.ComponentProps<'div'> {
  /**
   * Visual variant of the sidebar
   * @default 'sidebar'
   */
  variant?: SidebarVariant;

  /**
   * How the sidebar collapses
   * @default 'icon'
   */
  collapsible?: SidebarCollapsible;

  /**
   * Whether to show the sidebar rail (edge toggle button).
   * Set to false to hide the rail and prevent expand/collapse via the edge.
   * @default true
   */
  withRail?: boolean;
}

export type SidebarTriggerProps = React.ComponentProps<typeof Button>;

export interface SidebarRailProps extends React.ComponentProps<'button'> {
  /**
   * Accessible label for the rail button. Don't include the keyboard shortcut:
   * it is exposed via `aria-keyshortcuts` and appended to the `title` tooltip,
   * e.g. `Toggle Sidebar (H)`.
   *
   * When omitted, defaults to the translated "Toggle Sidebar" message.
   */
  label?: string;

  /**
   * Test identifier for debugging and testing
   */
  'data-testid'?: string;
}

export type SidebarInsetProps = React.ComponentProps<'main'>;

export type SidebarInputProps = React.ComponentProps<typeof Input>;

export type SidebarHeaderProps = React.ComponentProps<'div'>;

export type SidebarFooterProps = React.ComponentProps<'div'>;

export interface SidebarActionProps extends React.ComponentProps<'div'> {
  /**
   * Tooltip content to show when sidebar is collapsed
   */
  tooltip?: string;
}

export type SidebarSeparatorProps = React.ComponentProps<typeof Separator>;

export type SidebarContentProps = React.ComponentProps<'div'>;

export type SidebarGroupProps = React.ComponentProps<'div'>;

export interface SidebarGroupLabelProps extends React.ComponentProps<'div'> {
  /**
   * Render as a child element via Slot
   * @default false
   */
  asChild?: boolean;
}

export interface SidebarGroupActionProps extends React.ComponentProps<'button'> {
  /**
   * Render as a child element via Slot
   * @default false
   */
  asChild?: boolean;
}

export type SidebarGroupContentProps = React.ComponentProps<'div'>;

export type SidebarMenuProps = React.ComponentProps<'ul'>;

export interface SidebarMenuItemProps extends React.ComponentProps<'li'> {
  /**
   * Render as a child element via Slot.
   * Use this when the child contains a NavigationMenuItem (e.g., PanelFloating)
   * to avoid nested <li> elements.
   * @default false
   */
  asChild?: boolean;
}

export type SidebarMenuButtonSize = 'default' | 'sm' | 'lg';
export type SidebarMenuButtonVariant = 'default' | 'emphasis' | 'action';

export interface SidebarMenuButtonProps extends React.ComponentProps<'button'> {
  /**
   * Visual variant of the menu button
   * @default 'default'
   */
  variant?: SidebarMenuButtonVariant;

  /**
   * Size of the menu button
   * @default 'default'
   */
  size?: SidebarMenuButtonSize;
  /**
   * Render as a child element via Slot
   * @default false
   */
  asChild?: boolean;

  /**
   * Whether the menu button is currently active
   * @default false
   */
  isActive?: boolean;

  /**
   * Tooltip content to show when collapsed
   */
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;

  /**
   * Show tooltip even when sidebar is expanded.
   * Useful for icon-only buttons that don't have visible labels.
   * @default false
   */
  showTooltipWhenExpanded?: boolean;

  /**
   * Panel content to display when this menu button is hovered (desktop)
   * or when chevron is tapped (mobile).
   * If provided, the button will be wrapped with PanelFloating.
   */
  panelContent?: React.ReactNode;

  /**
   * Optional action button to display in the floating panel header.
   * Typically used for primary actions like "New chat", "Add task", etc.
   *
   * @example
   * ```tsx
   * <SidebarMenuButton
   *   panelContent={<TasksList />}
   *   panelAction={<Button variant="outline" size="sm"><Plus />Add task</Button>}
   * >
   *   Tasks
   * </SidebarMenuButton>
   * ```
   */
  panelAction?: React.ReactNode;

  /**
   * Optional callback when the panel's maximize button is clicked.
   * When provided, clicking maximize will navigate to the panel's page and pin it.
   * If not provided, maximize will only pin the panel without navigation.
   *
   * @example
   * ```tsx
   * <SidebarMenuButton
   *   panelContent={<TasksList />}
   *   panelOnNavigate={(pageId) => router.push(`/${pageId}`)}
   * >
   *   Tasks
   * </SidebarMenuButton>
   * ```
   */
  panelOnNavigate?: (pageId: string) => void;

  /**
   * Unique identifier for the panel.
   * Used for panel state management and pinning behavior.
   *
   * **Important for pinning:** To enable pinning for a page, this `panelId` must
   * match the `pageId` prop passed to `PageShell`. This ensures the correct panel
   * content is displayed when pinned on a specific page.
   *
   * If not provided, a generated ID is used (which won't support pinning).
   *
   * @example
   * ```tsx
   * // In PageShell (e.g., _app.tsx or layout)
   * <PageShell pageId={router.pathname}>
   *
   * // In sidebar nav item - panelId must match pageId for pinning
   * <SidebarMenuButton
   *   href="/scribe"
   *   panelId="/scribe"  // Matches pageId when on /scribe page
   *   panelContent={<ScribePanel />}
   * >
   *   Scribe
   * </SidebarMenuButton>
   * ```
   */
  panelId?: string;

  /**
   * Layout variant for label-icons mode.
   * Only applies when the sidebar collapsible is 'label-icons'.
   * - `default`: Full-width grid with icon + label (standard nav items)
   * - `iconOnly`: Compact 36px icon-only button (footer actions)
   * @default 'default'
   */
  layout?: 'default' | 'iconOnly';
}

export interface SidebarMenuActionProps extends React.ComponentProps<'button'> {
  /**
   * Render as a child element via Slot
   * @default false
   */
  asChild?: boolean;

  /**
   * Only show the action on hover
   * @default true
   */
  showOnHover?: boolean;
}

export type SidebarMenuBadgeProps = React.ComponentProps<'div'>;

export interface SidebarMenuSkeletonProps extends React.ComponentProps<'div'> {
  /**
   * Whether to show an icon skeleton
   * @default false
   */
  showIcon?: boolean;
}

export type SidebarMenuSubProps = React.ComponentProps<'ul'>;

export type SidebarMenuSubItemProps = React.ComponentProps<'li'>;

export type SidebarMenuSubButtonSize = 'sm' | 'md';

export interface SidebarMenuSubButtonProps extends React.ComponentProps<'a'> {
  /**
   * Render as a child element via Slot
   * @default false
   */
  asChild?: boolean;

  /**
   * Size of the sub button
   * @default 'md'
   */
  size?: SidebarMenuSubButtonSize;

  /**
   * Whether the sub button is currently active
   * @default false
   */
  isActive?: boolean;
}
